import { useEffect } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api, type Role } from '../../lib/api';

/** Tracker API types (crates/server/src/api/tracker.rs). */

export type IssueType = 'task' | 'bug' | 'story' | 'epic' | 'subtask';
export type Category = 'todo' | 'doing' | 'done';

export interface Status {
  id: string;
  name: string;
  category: Category;
  position: number;
  color: string;
  /** Entering it only from these statuses; null — from any. */
  allowedFrom: string[] | null;
  /** Fields an issue must have on entering it. */
  requiredFields: RequiredField[];
  /** Soft limit of issues in the board column; null — none. */
  wipLimit: number | null;
}

export const CATEGORIES: Category[] = ['todo', 'doing', 'done'];
export const REQUIRABLE = ['assignee', 'estimate', 'due', 'description', 'labels', 'sprint'] as const;
export type RequiredField = (typeof REQUIRABLE)[number];

export interface Sprint {
  id: string;
  /** The team whose sprint it is (docs/teams-plan.md). */
  teamId: string;
  teamName: string;
  name: string;
  goal: string;
  startsOn: string | null;
  endsOn: string | null;
  state: 'planned' | 'active' | 'closed';
  /** How much the team takes in: story points or hours, as the project estimates. */
  capacity: number | null;
  closedAt: string | null;
}

/** What a project's issues are estimated in. */
export type Estimation = 'points' | 'time';

export interface Person {
  id: string;
  name: string;
  color: string;
  /** An agent account (crates/server/src/api/agents.rs). */
  agent?: boolean;
}

export interface TrackerProject {
  id: string;
  teamId: string;
  estimation: Estimation;
  /** Its space (crates/server/src/api/spaces.rs) and the team's organization. */
  spaceId: string;
  spaceName: string;
  orgId: string | null;
  key: string;
  name: string;
  description: string;
  leadId: string | null;
  orgAccess: 'none' | 'view' | 'comment' | 'edit';
  archived: boolean;
  role: Role;
  /** The role in the project's own terms: admin, member, viewer, reporter. */
  projectRole?: 'admin' | 'member' | 'viewer' | 'reporter';
  /** May create issues (reporters too). */
  canReport?: boolean;
  /** The name of the sprint running now. */
  activeSprint?: string | null;
  /** The teams working in it, the home team first (docs/teams-plan.md). */
  teams?: ProjectTeam[];
  statuses?: Status[];
  sprints?: Sprint[];
  people?: Person[];
}

/** A team of a project: its people work in it (`member`) or read it (`viewer`). */
export interface ProjectTeam {
  id: string;
  name: string;
  role: 'member' | 'viewer';
  /** The project's own team: its space, its administration. */
  home: boolean;
}

/** A project worked in by more than one team: its issues say whose they are. */
export const sharedProject = (p: TrackerProject | null | undefined) => (p?.teams?.length ?? 0) > 1;

export interface Issue {
  id: string;
  projectId: string;
  key: string;
  number: number;
  type: IssueType;
  title: string;
  description: string;
  statusId: string;
  status: string;
  category: Category;
  priority: number;
  assigneeId: string | null;
  assigneeName: string | null;
  assigneeColor: string | null;
  reporterId: string | null;
  parentId: string | null;
  parentKey: string | null;
  sprintId: string | null;
  /** Whose issue it is: one of the project's teams. */
  teamId: string | null;
  estimateMinutes: number | null;
  /** Story points. */
  points: number | null;
  spentMinutes: number;
  dueOn: string | null;
  labels: string[];
  rank: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  /** An open issue blocks it. */
  blocked?: boolean;
  /** How many files are attached. */
  attachments?: number;
  /** Imported issues: where from, and people's names there that matched nobody here. */
  source?: { system: 'jira' | 'youtrack' | 'yandex' | 'kaiten' | 'trello' | 'csv'; key: string; url: string; assignee?: string; reporter?: string } | null;
  /** Other fields an import brought, as text: name → value. */
  custom?: Record<string, string> | null;
  role?: Role;
}

export interface IssueComment {
  id: string;
  authorId: string | null;
  authorName: string | null;
  authorColor: string | null;
  body: string;
  createdAt: string;
  editedAt: string | null;
  /** Written in the system the issue came from by someone who is not here. */
  imported?: boolean;
  /** People the text's "@Name"s point at. */
  mentions?: string[];
}

export type LinkKind = 'blocks' | 'relates' | 'duplicates';

/** A link as this issue sees it: outgoing — "blocks PAY-2", incoming — "blocked by PAY-1". */
export interface IssueLink {
  kind: LinkKind;
  outgoing: boolean;
  issue: Issue;
}

/** How a link reads from one issue's side. */
export type Relation = 'blocks' | 'blocked_by' | 'relates' | 'duplicates' | 'duplicated_by';
export const RELATIONS: Relation[] = ['blocks', 'blocked_by', 'relates', 'duplicates', 'duplicated_by'];

export function relationOf(l: Pick<IssueLink, 'kind' | 'outgoing'>): Relation {
  if (l.kind === 'relates') return 'relates';
  if (l.kind === 'blocks') return l.outgoing ? 'blocks' : 'blocked_by';
  return l.outgoing ? 'duplicates' : 'duplicated_by';
}

export interface Watchers {
  watchers: Person[];
  watching: boolean;
}

export interface HistoryEntry {
  actorId: string | null;
  actorName: string | null;
  at: string;
  field: string;
  old: unknown;
  new: unknown;
}

export const TYPES: IssueType[] = ['task', 'bug', 'story', 'epic', 'subtask'];
/** 0 critical … 3 low. */
export const PRIORITIES = [0, 1, 2, 3] as const;
export const PRIORITY_COLORS = ['#E5484D', '#F76B15', '#8A8F98', '#3E9B6F'];
export const TYPE_COLORS: Record<IssueType, string> = { task: '#3A6FD8', bug: '#E5484D', story: '#2FA36B', epic: '#8E5CE6', subtask: '#8A8F98' };

export const keys = {
  projects: ['tracker', 'projects'] as const,
  project: (id: string) => ['tracker', 'project', id] as const,
  issues: (project: string | null) => ['tracker', 'issues', project] as const,
  issue: (key: string) => ['tracker', 'issue', key] as const,
  archived: ['tracker', 'projects', 'archived'] as const,
  links: (key: string) => ['tracker', 'links', key] as const,
  watchers: (key: string) => ['tracker', 'watchers', key] as const,
  attachments: (key: string) => ['tracker', 'attachments', key] as const,
  filters: ['tracker', 'filters'] as const,
  development: (key: string) => ['tracker', 'development', key] as const,
  gitRepos: (team: string) => ['git', 'repos', team] as const,
  worklogs: (key: string) => ['tracker', 'worklogs', key] as const,
  time: (project: string, from: string, to: string) => ['tracker', 'time', project, from, to] as const,
  team: (id: string) => ['tracker', 'team', id] as const,
  /** Under `issues(null)`: what invalidates every issue list reaches the team's too. */
  teamIssues: (id: string, q: string) => ['tracker', 'issues', null, 'team', id, q] as const,
};

export function useProjects() {
  return useQuery({ queryKey: keys.projects, queryFn: () => api.get<TrackerProject[]>('/tracker/projects') });
}

export function useArchivedProjects() {
  return useQuery({ queryKey: keys.archived, queryFn: () => api.get<TrackerProject[]>('/tracker/projects?archived=true') });
}

/**
 * A team's plans (crates/server/src/api/tracker.rs, get_team): its sprints, the projects it works
 * in that the person sees (with their statuses), its people and what the person may do.
 */
export interface TeamPlan {
  id: string;
  name: string;
  orgId: string | null;
  personal: boolean;
  parallelSprints: boolean;
  member: boolean;
  canPlan: boolean;
  /** Renames or deletes it: its owners and admins, and the organization's. */
  canManage: boolean;
  /** Story points unless every project of the team estimates in time. */
  estimation: Estimation;
  sprints: Sprint[];
  projects: TrackerProject[];
  people: Person[];
}

/** For the menu: the person's teams that work with sprints, each with its running sprint. */
export function useTeamSprints() {
  return useQuery({ queryKey: ['tracker', 'team', 'sprints'], queryFn: () => api.get<{ teamId: string; active: string | null }[]>('/tracker/team-sprints'), staleTime: 60_000 });
}

export function useTeamPlan(id: string | null) {
  return useQuery({ queryKey: keys.team(id ?? ''), queryFn: () => api.get<TeamPlan>(`/tracker/teams/${id}`), enabled: !!id });
}

/** A team's issues, as a project's view loads its own: in the shared order (board, backlog). */
export function useTeamIssues(id: string, q = '') {
  return useQuery({
    queryKey: keys.teamIssues(id, q),
    queryFn: () => api.get<IssueList>(`/tracker/issues?${new URLSearchParams({ team: id, q, order: 'rank', limit: '500' })}`),
  });
}

export function useProject(id: string | null) {
  return useQuery({ queryKey: keys.project(id ?? ''), queryFn: () => api.get<TrackerProject>(`/tracker/projects/${id}`), enabled: !!id });
}

export interface IssueList {
  issues: Issue[];
  warnings: string[];
}

/** Issues of a project (or of every project) matching a query in the tracker's language. */
export function useIssues(project: string | null, q: string, order: 'rank' | 'default' = 'default') {
  return useQuery({
    queryKey: [...keys.issues(project), q, order],
    queryFn: () => {
      const s = new URLSearchParams({ q, limit: '500' });
      if (project) s.set('project', project);
      if (order === 'rank') s.set('order', 'rank');
      return api.get<IssueList>(`/tracker/issues?${s}`);
    },
    placeholderData: (prev) => prev,
  });
}

/**
 * An issue as the API addresses it. Keys repeat across organizations (two PAY-12 for someone in
 * both), so requests go by id; caches stay keyed by the key, which is what events carry.
 */
export interface IssueRef {
  id: string;
  key: string;
}

/** The API path of an issue (and of what hangs off it). */
export const issuePath = (issue: IssueRef, rest = '') => `/tracker/issues/${issue.id}${rest}`;

/** An issue by key; `projectId` says which one when the key repeats across organizations. */
export function useIssue(key: string | null, projectId?: string) {
  const scope = projectId ? `?project=${projectId}` : '';
  return useQuery({
    queryKey: [...keys.issue(key ?? ''), projectId ?? ''],
    queryFn: () => api.get<Issue>(`/tracker/issues/${encodeURIComponent(key ?? '')}${scope}`),
    enabled: !!key,
  });
}

export const patchIssue = (issue: IssueRef, patch: Record<string, unknown>) => api.patch<Issue>(issuePath(issue), patch);

/** What to refetch after a change: lists of its project, the issue, the project (sprints). */
export function invalidateIssue(qc: QueryClient, projectId: string, key?: string) {
  void qc.invalidateQueries({ queryKey: keys.issues(projectId) });
  void qc.invalidateQueries({ queryKey: keys.issues(null) });
  if (key) {
    void qc.invalidateQueries({ queryKey: keys.issue(key) });
    void qc.invalidateQueries({ queryKey: ['tracker', 'comments', key] });
    void qc.invalidateQueries({ queryKey: ['tracker', 'history', key] });
    void qc.invalidateQueries({ queryKey: keys.links(key) });
    void qc.invalidateQueries({ queryKey: keys.watchers(key) });
    void qc.invalidateQueries({ queryKey: keys.attachments(key) });
    void qc.invalidateQueries({ queryKey: keys.development(key) });
  } else {
    void qc.invalidateQueries({ queryKey: ['tracker', 'development'] });
  }
}

export function useLinks(issue: IssueRef) {
  return useQuery({ queryKey: keys.links(issue.key), queryFn: () => api.get<IssueLink[]>(issuePath(issue, '/links')) });
}

export interface Attachment {
  id: string;
  issueId: string;
  name: string;
  mime: string;
  size: number;
  createdBy: string | null;
  authorName: string | null;
  createdAt: string;
}

/** Largest file from the browser (the edge proxy's limit; the server says the same). */
export const MAX_UPLOAD = 25 * 1024 * 1024;

export function useAttachments(issue: IssueRef) {
  return useQuery({ queryKey: keys.attachments(issue.key), queryFn: () => api.get<Attachment[]>(issuePath(issue, '/attachments')) });
}

export const attachmentUrl = (id: string, download = false) => `/api/tracker/attachments/${id}${download ? '?download=1' : ''}`;

/** Images the browser shows in place (the server serves these inline). */
export const isPreviewable = (mime: string) => ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/avif'].includes(mime);

/** 1536 → "1,5 КБ" (units from the dictionary, numbers in the reader's locale). */
export function formatSize(bytes: number, t: (k: string) => string, locale?: string): string {
  const units = ['b', 'kb', 'mb', 'gb'];
  let n = bytes, i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  const digits = i === 0 || n >= 10 ? 0 : 1;
  return `${n.toLocaleString(locale, { maximumFractionDigits: digits })} ${t(`tracker.units.${units[i]}`)}`;
}

/** A branch, commit or pull request that names the issue (crates/server/src/git). */
export interface DevRef {
  id: string;
  kind: 'branch' | 'commit' | 'pr';
  extId: string;
  title: string;
  url: string;
  author: string | null;
  /** PR: open, draft, merged, closed. Branch: active, deleted. */
  state: string | null;
  ciState: 'pending' | 'running' | 'success' | 'failed' | 'canceled' | null;
  ciUrl: string | null;
  repo: string;
  provider: 'github' | 'gitlab' | 'gitea' | 'bitbucket';
  updatedAt: string;
}

export function useDevelopment(issue: IssueRef) {
  return useQuery({ queryKey: keys.development(issue.key), queryFn: () => api.get<DevRef[]>(issuePath(issue, '/development')) });
}

export interface GitRepo {
  id: string;
  teamId: string;
  provider: 'github' | 'gitlab' | 'gitea' | 'bitbucket';
  name: string;
  url: string;
  autoClose: boolean;
  createdAt: string;
  lastEventAt: string | null;
  lastEvent: string | null;
  canManage: boolean;
  /** Only for the team's owners and admins. */
  secret?: string;
  hookUrl: string;
  linkedIssues: number;
}

export function useGitRepos(team: string) {
  return useQuery({ queryKey: keys.gitRepos(team), queryFn: () => api.get<GitRepo[]>(`/git/repos?team=${team}`) });
}

/** A saved search (crates/server/src/tracker/filters.rs). */
export interface SavedFilter {
  id: string;
  ownerId: string;
  ownerName: string;
  /** Shared with this team; null: only its owner sees it. */
  teamId: string | null;
  teamName: string | null;
  /** Searched within this project; null: across all. */
  projectId: string | null;
  projectKey: string | null;
  name: string;
  query: string;
  mine: boolean;
  canEdit: boolean;
  /** The reader gets its issues by email. */
  subscription: 'daily' | 'weekly' | null;
  createdAt: string;
  updatedAt: string;
}

/** Saved filters of an organization (`null`: the personal space). */
export function useFilters(org: string | null, enabled = true) {
  const scope = org ?? 'personal';
  return useQuery({ queryKey: [...keys.filters, 'org', scope], queryFn: () => api.get<SavedFilter[]>(`/tracker/filters?org=${scope}`), staleTime: 30_000, enabled });
}

/** One change for many issues; each is checked on its own (crates/server/src/api/tracker.rs). */
export interface BulkChange {
  statusId?: string;
  statusName?: string;
  assigneeId?: string | null;
  priority?: number;
  sprintId?: string | null;
  teamId?: string | null;
  addLabels?: string[];
  removeLabels?: string[];
  delete?: boolean;
}

export interface BulkResult {
  results: { id: string; key?: string; ok: boolean; changed?: boolean; error?: string }[];
  changed: number;
  failed: number;
}

export const bulkEdit = (ids: string[], change: BulkChange) => api.post<BulkResult>('/tracker/issues/bulk', { ids, ...change });

/** Statuses every one of these projects has (by name, case aside), in the first project's order. */
export function commonStatuses(projects: Pick<TrackerProject, 'statuses'>[]): Status[] {
  const [first, ...rest] = projects;
  if (!first?.statuses) return [];
  return first.statuses.filter((s) => rest.every((p) => p.statuses?.some((x) => x.name.toLowerCase() === s.name.toLowerCase())));
}

/** People who can be assignees in every one of these projects. */
export function commonPeople(projects: Pick<TrackerProject, 'people'>[]): Person[] {
  const [first, ...rest] = projects;
  if (!first?.people) return [];
  return first.people.filter((p) => rest.every((x) => x.people?.some((y) => y.id === p.id)));
}

export function useWatchers(issue: IssueRef) {
  return useQuery({ queryKey: keys.watchers(issue.key), queryFn: () => api.get<Watchers>(issuePath(issue, '/watchers')) });
}

/** Whether an issue in `from` may move to `to` by that status's rule (required fields aside). */
export function canEnter(to: Status, from: string): boolean {
  return to.id === from || !to.allowedFrom || to.allowedFrom.includes(from);
}

/** People whose "@Name" is in the text, longest names first so "@Анна Петрова" wins over "@Анна". */
export function mentionedIn(text: string, people: Person[]): Person[] {
  return [...people].sort((a, b) => b.name.length - a.name.length).filter((p) => text.includes(`@${p.name}`));
}

/**
 * Live changes of a project (server-sent events, published on every node of the cluster):
 * whatever someone else changes refetches here within a moment.
 */
export function useProjectEvents(projectId: string | null) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!projectId) return;
    const es = new EventSource(`/api/tracker/projects/${projectId}/events`);
    es.onmessage = (m) => {
      let ev: { type?: string; key?: string } = {};
      try {
        ev = JSON.parse(m.data);
      } catch {
        return;
      }
      if (ev.type === 'issue' || ev.type === 'comment') invalidateIssue(qc, projectId, ev.key);
      else {
        void qc.invalidateQueries({ queryKey: keys.project(projectId) });
        // Sprints are the teams': their pages show them too.
        void qc.invalidateQueries({ queryKey: ['tracker', 'team'] });
        invalidateIssue(qc, projectId);
      }
    };
    return () => es.close();
  }, [projectId, qc]);
}

/** Minutes as a short estimate: 90 → "1ч 30м", 960 → "2д" (an 8-hour day). */
export function formatMinutes(m: number | null, t: (k: string) => string): string {
  if (m === null || m === undefined) return '';
  const d = Math.floor(m / 480), h = Math.floor((m % 480) / 60), min = m % 60;
  const parts: string[] = [];
  if (d) parts.push(`${d}${t('tracker.units.d')}`);
  if (h) parts.push(`${h}${t('tracker.units.h')}`);
  if (min || parts.length === 0) parts.push(`${min}${t('tracker.units.m')}`);
  return parts.join(' ');
}

/** "3д", "2ч 30м", "45м", "1d 4h" → minutes; null when it does not parse. */
export function parseMinutes(s: string): number | null {
  const src = s.trim().toLowerCase();
  if (!src) return null;
  let total = 0, matched = false;
  for (const m of src.matchAll(/(\d+(?:[.,]\d+)?)\s*(д|d|ч|h|м|m)/g)) {
    const n = parseFloat(m[1].replace(',', '.'));
    total += m[2] === 'д' || m[2] === 'd' ? n * 480 : m[2] === 'ч' || m[2] === 'h' ? n * 60 : n;
    matched = true;
  }
  if (!matched && /^\d+$/.test(src)) return parseInt(src, 10) * 60;
  return matched ? Math.round(total) : null;
}

export function isOverdue(i: Pick<Issue, 'dueOn' | 'category'>): boolean {
  if (!i.dueOn || i.category === 'done') return false;
  return i.dueOn < today();
}

/** Today in the person's time zone, as YYYY-MM-DD. */
export function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------------------------
// Time tracking

export interface Worklog {
  id: string;
  issueId: string;
  userId: string | null;
  authorName: string | null;
  authorColor: string | null;
  minutes: number;
  spentOn: string;
  note: string;
  createdAt: string;
}

export function useWorklogs(issue: IssueRef) {
  return useQuery({ queryKey: keys.worklogs(issue.key), queryFn: () => api.get<Worklog[]>(issuePath(issue, '/worklogs')) });
}

/** One line of the project's time report: a person's time on an issue on a day. */
export interface TimeRow {
  userId: string | null;
  name: string | null;
  color: string | null;
  issueKey: string;
  issueTitle: string;
  spentOn: string;
  minutes: number;
}

/** `YYYY-MM-DD` of a local date. */
export function isoDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** The days of the week (Monday first) that holds `d`, `shift` weeks away. */
export function weekOf(d: Date, shift = 0): string[] {
  const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - ((d.getDay() + 6) % 7) + shift * 7);
  return Array.from({ length: 7 }, (_, i) => isoDay(new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i)));
}

export interface TimeSheetIssue {
  key: string;
  title: string;
  days: number[];
  total: number;
}

export interface TimeSheetPerson {
  id: string;
  name: string;
  color: string | null;
  days: number[];
  total: number;
  issues: TimeSheetIssue[];
}

/** Report rows into a sheet: people (by name) × the given days, each with its issues. */
export function timeSheet(rows: TimeRow[], days: string[]): { people: TimeSheetPerson[]; days: number[]; total: number } {
  const at = new Map(days.map((d, i) => [d, i]));
  const people = new Map<string, TimeSheetPerson>();
  const totals = days.map(() => 0);
  for (const r of rows) {
    const i = at.get(r.spentOn);
    if (i === undefined) continue;
    const id = r.userId ?? `name:${r.name ?? ''}`;
    let p = people.get(id);
    if (!p) {
      p = { id, name: r.name ?? '', color: r.color, days: days.map(() => 0), total: 0, issues: [] };
      people.set(id, p);
    }
    let issue = p.issues.find((x) => x.key === r.issueKey);
    if (!issue) {
      issue = { key: r.issueKey, title: r.issueTitle, days: days.map(() => 0), total: 0 };
      p.issues.push(issue);
    }
    issue.days[i] += r.minutes;
    issue.total += r.minutes;
    p.days[i] += r.minutes;
    p.total += r.minutes;
    totals[i] += r.minutes;
  }
  const list = [...people.values()].sort((a, b) => a.name.localeCompare(b.name));
  for (const p of list) p.issues.sort((a, b) => b.total - a.total);
  return { people: list, days: totals, total: totals.reduce((a, b) => a + b, 0) };
}
